import React, { Fragment, useEffect } from "react";
import logo from "./logo.png";
import { Layout, Menu, Avatar, Typography, Badge, Tooltip } from "antd";
import {
  HomeOutlined,
  LogoutOutlined,
  CalendarOutlined,
  FileDoneOutlined,
  ContactsOutlined,
  GiftOutlined,
  MedicineBoxOutlined,
  SkinOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  UserOutlined,
  SendOutlined,
  NotificationOutlined,
  WalletOutlined,
  SwapOutlined,
  UsergroupAddOutlined,
  TeamOutlined,
  ShopOutlined,
  ProfileOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { connect } from "react-redux";
import { ApolloResult, AppState, Clinic, User, actions } from "../store";
import paths from "../routes/paths";
import { useLazyQuery } from "@apollo/client";
import { getClinicVariables, get_clinics } from "../graphql/clinic";
import SubMenu from "antd/lib/menu/SubMenu";
import { appVersion } from "../constants";
import { Divider } from "antd";
const { Sider } = Layout;
const { Text } = Typography;

interface P {
  onMenuClik: (item: { key: string }) => void;
  openSider: boolean;
  currentMenuItem?: string;
  currentClinic?: Clinic;
  clinicIds: string[];
  clinics: Clinic[];
  setClinics: (c: Clinic[]) => void;
  setSelectedClinic: (c: Clinic) => void;
  user?: User;
  logout: () => void;
}

const menuList = [
  {
    name: "Home",
    path: paths.home,
    icon: HomeOutlined,
    isMain: true,
    badge: null,
  },
  {
    name: "Appointments",
    path: paths.appointments,
    icon: CalendarOutlined,
    isMain: true,
    badge: null,
  },
  {
    name: "Check In/Out",
    path: paths.checkIns,
    icon: ClockCircleOutlined,
    isMain: true,
    badge: null,
  },
  {
    name: "Sales Report",
    path: paths.sales,
    icon: FileDoneOutlined,
    isMain: true,
    badge: null,
  },
  {
    name: "Sale Channel",
    path: paths.sale_channel,
    icon: ShopOutlined,
    isMain: true,
    badge: null,
    links: [],
  },
  {
    name: "Reports",
    path: paths.report,
    icon: FileTextOutlined,
    isMain: false,
    badge: null,
    links: [
      { path: paths.daily_sales, name: "Daily Sale Report" },
      { path: paths.gtgram_sales, name: "GTGram Sales" },
      { path: paths.treatment_report, name: "Treatment Report" },
      { path: paths.top_treatment_report, name: "Top Treatment Report" },
      { path: paths.top_practitioner_report, name: "Top Practitioner Report" },
      { path: paths.seller_report, name: "Seller Report" },
      { path: paths.credit_received_report, name: "Credit Received Report" },
    ],
  },
  {
    name: "Services",
    path: paths.services,
    icon: MedicineBoxOutlined,
    isMain: false,
    badge: null,
    links: [
      { path: paths.services, name: "Service List" },
      { path: paths.service_packages, name: "Service Packages" },
      { path: paths.service_categories, name: "Service Type Category" },
      { path: paths.service_record_froms, name: "Service Record Form" },
      { path: paths.service_consent_froms, name: "Service Consent Form" },
    ],
  },
  {
    name: "Products",
    path: paths.products,
    icon: SkinOutlined,
    isMain: false,
    badge: null,
    links: [
      { path: paths.products, name: "Product List" },
      { path: paths.product_stock_items, name: "Product Stock Items" },
    ],
  },
  {
    name: "Therapists",
    path: paths.therapists,
    icon: ContactsOutlined,
    isMain: true,
    badge: null,
  },
  {
    name: "Helper",
    path: paths.helper,
    icon: UsergroupAddOutlined,
    isMain: true,
    badge: null,
  },
  {
    name: "Members",
    path: paths.members,
    icon: UserOutlined,
    isMain: true,
    badge: null,
  },
  {
    name: "Wallets",
    path: paths.wallets,
    icon: WalletOutlined,
    isMain: true,
    badge: null,
  },
  {
    name: "Transactions",
    path: paths.transactions,
    icon: SwapOutlined,
    isMain: true,
    badge: null,
  },
  {
    name: "Offers",
    path: paths.offers,
    icon: SendOutlined,
    isMain: false,
    badge: null,
    links: [
      { path: paths.offer_categories, name: "Offer Category" },
      { path: paths.offers, name: "Offer List" },
    ],
  },
  {
    name: "Notifications",
    path: paths.notifications,
    icon: NotificationOutlined,
    isMain: true,
    badge: null,
    links: [],
  },
  {
    name: "Leaves",
    path: paths.leaves,
    icon: CalendarOutlined,
    isMain: true,
    badge: null,
    links: [],
  },
  {
    name: "Users",
    path: paths.user,
    icon: TeamOutlined,
    isMain: true,
    badge: null,
    links: [],
  },
  {
    name: "Logs",
    path: paths.logs,
    icon: ProfileOutlined,
    isMain: true,
    badge: null,
    links: [],
  },
];

function SideNav(props: P) {
  const [getClinics, { data: cData, error: cError, loading: cLoading }] =
    useLazyQuery<ApolloResult<"clinics", Clinic[]>>(get_clinics, {
      variables: getClinicVariables(props.clinicIds),
    });

  useEffect(() => {
    if (cData?.clinics) {
      localStorage.setItem("clinics", JSON.stringify(cData.clinics));
      props.setClinics(cData.clinics);
    }
  }, [cData]);

  useEffect(() => {
    const localClinics = localStorage.getItem("clinics");
    if (localClinics != null) {
      const clinics = JSON.parse(localClinics);
      props.setClinics(clinics);
    } else {
      getClinics();
    }
  }, []);

  const renderMenuItem = (item: any, index: number) => {
    const IconComponent = item.icon;
    const isCollapsed = props.openSider;
    
    // For collapsed state, render all items as simple menu items
    if (isCollapsed) {
      return (
        <Menu.Item 
          key={item.path || `sub-menu_${index}`}
          icon={<IconComponent />}
          style={styles.menuItem}
        >
          <div style={styles.menuItemContent}>
            <span>{item.name}</span>
          </div>
        </Menu.Item>
      );
    }
    
    // For expanded state, render with proper structure
    if (item.isMain) {
      return (
        <Menu.Item 
          key={item.path} 
          icon={<IconComponent />}
          style={styles.menuItem}
        >
          <div style={styles.menuItemContent}>
            <span>{item.name}</span>
            {item.badge && (
              <Badge 
                count={item.badge} 
                size="small" 
                style={styles.badge}
              />
            )}
          </div>
        </Menu.Item>
      );
    } else {
      return (
        <SubMenu
          key={`sub-menu_${index}`}
          icon={<IconComponent />}
          title={
            <div style={styles.menuItemContent}>
              <span>{item.name}</span>
              {item.badge && (
                <Badge 
                  count={item.badge} 
                  size="small" 
                  style={styles.badge}
                />
              )}
            </div>
          }
          style={styles.subMenu}
        >
          {item.links?.map((sub: any) => (
            <Menu.Item 
              key={sub.path}
              style={styles.subMenuItem}
            >
              <span>{sub.name}</span>
            </Menu.Item>
          ))}
        </SubMenu>
      );
    }
  };

  return (
    <React.Fragment>
      <Sider
        className="side-nav"
        collapsible
        collapsed={props.openSider}
        trigger={null}
        width={280}
        style={styles.sider}
        data-collapsed={props.openSider ? "true" : "false"}
      >
        {/* User Profile Section - Fixed at top */}
        <div style={styles.profileSection}>
          <div style={styles.profileContent}>
            <Avatar
              size={props.openSider ? 40 : 50}
              src={props.user?.photo || logo}
              style={styles.avatar}
            />
            {!props.openSider && (
              <div style={styles.userInfo}>
                <Text strong style={styles.userName}>
                  {props.user?.name || "Admin"}
                </Text>
                <Text type="secondary" style={styles.userRole}>
                  Administrator
                </Text>
                <Text type="secondary" style={styles.version}>
                  {appVersion}
                </Text>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Menu - Scrollable area */}
        <div style={styles.menuContainer} className="menuContainer">
          <Menu
            theme="light"
            mode="inline"
            defaultSelectedKeys={
              props.currentMenuItem ? [props.currentMenuItem] : [paths.home]
            }
            onClick={props.onMenuClik}
            style={styles.menu}
            className={props.openSider ? "collapsed-menu" : "expanded-menu"}
          >
            {menuList.map((item, index) => renderMenuItem(item, index))}
          </Menu>
        </div>

        {/* Footer Section - Fixed at bottom */}
        <div style={styles.footer}>
          {!props.openSider && (
            <div style={styles.footerContent}>
              <Text type="secondary" style={styles.footerText}>
                GT Report System
              </Text>
            </div>
          )}
          <div style={styles.logoutContainer}>
            <Menu
              style={styles.logoutMenu}
              onClick={props.logout}
            >
              <Menu.Item 
                icon={<LogoutOutlined style={props.openSider ? { fontSize: 18, marginRight: 0 } : { fontSize: 16 }} />}
                style={styles.logoutItem}
              >
                {!props.openSider && <span>Logout</span>}
              </Menu.Item>
            </Menu>
          </div>
        </div>
      </Sider>
    </React.Fragment>
  );
}

const mapStateToDispatch = (dispatch: any) => {
  return {
    setClinics: (d: Clinic[]) => dispatch(actions.clinicAction.setClinics(d)),
    setSelectedClinic: (d: Clinic) =>
      dispatch(actions.clinicAction.setSelected(d)),
    logout: () => dispatch(actions.authAction.loggedOut()),
  };
};

const mapStateToProps = (state: AppState) => {
  return {
    openSider: state.antdState.openSideToogle,
    currentClinic: state.clinicState.selected,
    clinicIds:
      state.authState.status === "loggedIn"
        ? state.authState.userInfo?.clinics || []
        : [],
    clinics: state.clinicState.clinics,
    user:
      state.authState.status === "loggedIn"
        ? state.authState.userInfo!
        : undefined,
  };
};

export default connect(mapStateToProps, mapStateToDispatch)(SideNav) as any;

const styles = {
  sider: {
    background: "#ffffff",
    borderRight: "1px solid #f0f0f0",
    boxShadow: "2px 0 8px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column" as const,
    height: "100vh",
    position: "relative" as const,
  },
  profileSection: {
    padding: "16px 16px",
    borderBottom: "1px solid #f0f0f0",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    flexShrink: 0,
    position: "sticky" as const,
    top: 0,
    zIndex: 10,
    minHeight: "70px",
    maxHeight: "80px",
  },
  profileContent: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  avatar: {
    border: "2px solid rgba(255,255,255,0.3)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    width: "40px",
    height: "40px",
  },
  userInfo: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "4px",
  },
  userName: {
    color: "white",
    fontSize: "15px",
    fontWeight: 600,
    margin: 0,
  },
  userRole: {
    color: "rgba(255,255,255,0.8)",
    fontSize: "12px",
    margin: 0,
  },
  version: {
    color: "rgba(255,255,255,0.6)",
    fontSize: "10px",
    margin: 0,
  },
  menuContainer: {
    flex: 1,
    overflowY: "auto" as const,
    overflowX: "hidden" as const,
    padding: "16px 0 80px 0",
    minHeight: 0,
    maxHeight: "calc(100vh - 200px)",
    display: "flex",
    flexDirection: "column" as const,
  },
  menu: {
    border: "none",
    background: "transparent",
  },
  menuItem: {
    margin: "4px 12px",
    borderRadius: "8px",
    height: "44px",
    lineHeight: "44px",
    padding: "0 12px",
    transition: "all 0.3s ease",
  },
  menuItemContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  subMenu: {
    margin: "4px 12px",
  },
  subMenuItem: {
    margin: "2px 0",
    paddingLeft: "48px",
    height: "36px",
    lineHeight: "36px",
    fontSize: "13px",
  },
  badge: {
    backgroundColor: "#1890ff",
    fontSize: "10px",
  },
  footer: {
    padding: "12px 16px",
    borderTop: "1px solid #f0f0f0",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column" as const,
    gap: "6px",
    position: "absolute" as const,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#ffffff",
    zIndex: 10,
    minHeight: "60px",
  },
  footerDivider: {
    margin: "0 0 8px 0",
  },
  footerContent: {
    textAlign: "center" as const,
    marginBottom: "4px",
  },
  footerText: {
    fontSize: "11px",
    color: "#8c8c8c",
  },
  logoutContainer: {
    display: "flex",
    justifyContent: "center",
  },
  logoutMenu: {
    background: "transparent",
    border: "none",
    width: "100%",
  },
  logoutItem: {
    borderRadius: "8px",
    margin: "0",
    height: "36px",
    lineHeight: "36px",
    textAlign: "center",
    backgroundColor: "#1890ff",
    color: "white",
    border: "none",
    fontSize: "14px",
  },
};
